import { FormLabel, InputAdornment, Popover, TextField } from '@mui/material';
import { ChevronDown, ChevronUp, Search, X } from 'lucide-react';
import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CreateButton,
  DropdownContainer,
  DropdownList,
  OptionItem,
  OptionsList,
  Root,
  SelectedValue,
  SelectedValues,
} from './styles';

export interface Option {
  id: string | number;
  label?: string;
  name?: string;
  title?: string;
  type?: string;
  style?: React.CSSProperties;
  [key: string]: unknown;
}

interface SelectEntitiesProps {
  label?: string;
  options?: Option[];
  placeholder?: string;
  values: Option[];
  onChange?: (name: string, option: Option) => void;
  onRemove?: (name: string, value: Option) => void;
  name?: string;
  dropdownLabel?: string;
  optionComponent: (option: Option) => React.ReactNode;
  url?: string;
  cleanUpAction?: (payload: { location: string | number; type: string | null }) => void;
  hideValues?: boolean;
  disabled?: boolean;
  onCreateClick?: () => void;
  onValueClick?: (value: Option) => void;
}

const SelectEntities: React.FC<SelectEntitiesProps> = ({
  label,
  options = [],
  placeholder = 'Select an option',
  values,
  onChange,
  onRemove,
  name,
  dropdownLabel,
  optionComponent,
  url: _url,
  cleanUpAction,
  hideValues,
  disabled,
  onCreateClick,
  onValueClick,
}) => {
  const { t } = useTranslation('dashboard', { keyPrefix: 'global' });

  const anchorEl = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [searchValue, setSearchValue] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [popoverWidth, setPopoverWidth] = useState<number | undefined>(undefined);

  const handleClick = () => {
    if (!disabled) {
      if (anchorEl.current) {
        setPopoverWidth(anchorEl.current.offsetWidth);
      }
      setIsOpen(true);
      // Keep focus on the input field
      setTimeout(() => {
        inputRef.current?.focus();
      }, 0);
    }
  };

  const handleClose = (event: unknown, reason: 'backdropClick' | 'escapeKeyDown') => {
    // Don't close if clicking on the TextField or its container
    if (reason === 'backdropClick') {
      const target = (event as React.MouseEvent).target as Node;
      if (anchorEl.current?.contains(target)) {
        return;
      }
    }
    setIsOpen(false);
  };

  const handleOptionClick = (option: Option) => {
    if (onChange) {
      onChange(name || '', option);
      setIsOpen(false);
      setSearchValue('');
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
  };

  const filteredOptions = useMemo(
    () =>
      options?.filter?.(
        (option) =>
          [option.label, option.name, option.title].some((i) =>
            i?.toLowerCase().includes(searchValue.toLowerCase()),
          ) && !values.some((i) => i.id === option.id),
      ) || [],
    [options, searchValue, values],
  );

  const handleRemove = (value: Option) => (e: React.MouseEvent) => {
    e?.preventDefault();
    e?.stopPropagation();
    if (disabled || !onRemove) return;
    onRemove(name || '', value);
  };

  const handleValueClick = (value: Option) => {
    if (disabled) return;
    cleanUpAction?.({
      location: value.id,
      type: value.type === 'quote' ? 'quote' : null,
    });
    onValueClick?.(value);
  };

  const openPopover = Boolean(isOpen && anchorEl.current);

  return (
    <Root disabled={disabled}>
      {label && <FormLabel>{label}</FormLabel>}
      <DropdownContainer ref={anchorEl}>
        <TextField
          fullWidth
          disabled={disabled}
          value={searchValue}
          autoComplete='off'
          onFocus={handleClick}
          onChange={handleSearchChange}
          placeholder={placeholder}
          inputRef={inputRef}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position='start'>
                  <Search size={16} />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position='end'>
                  {isOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                </InputAdornment>
              ),
            },
          }}
        />
        <Popover
          open={openPopover}
          anchorEl={anchorEl.current}
          onClose={handleClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          disableAutoFocus
          disableEnforceFocus
          disableRestoreFocus
          slotProps={{
            backdrop: {
              invisible: true,
            },
            paper: {
              sx: {
                marginTop: '4px',
                marginRight: '4px',
                width: popoverWidth ? `${popoverWidth}px` : 'auto',
                maxWidth: '100%',
              },
              onClick: (e: React.MouseEvent) => {
                e.preventDefault();
                e.stopPropagation();
              },
            },
          }}
        >
          <DropdownList>
            {dropdownLabel && <FormLabel>{dropdownLabel}</FormLabel>}
            <OptionsList>
              {filteredOptions.map((option, index) => (
                <OptionItem
                  key={option.id || index}
                  onClick={() => handleOptionClick(option)}
                  style={option.style}
                >
                  {optionComponent(option)}
                </OptionItem>
              ))}
            </OptionsList>
            {onCreateClick && (
              <CreateButton variant='outlined' color='primary' onClick={onCreateClick}>
                {t('create_new')}
              </CreateButton>
            )}
          </DropdownList>
        </Popover>
      </DropdownContainer>
      <SelectedValues>
        {!hideValues &&
          values.map((value) => {
            return (
              <SelectedValue onClick={() => handleValueClick(value)} key={value.id}>
                {optionComponent(value)}
                <X
                  size={16}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleRemove(value)(e);
                  }}
                />
              </SelectedValue>
            );
          })}
      </SelectedValues>
    </Root>
  );
};

export default SelectEntities;
